import type { RecentlyUsedSymbolsService } from './index';
import { ChronoUnit, Instant } from '@js-joda/core';
import { signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { injectable } from 'src/features/ioc';
import { SYMBOL_CACHE_KEY } from 'src/lib/constants/storage-keys';
import { localforage } from 'src/lib/serialization/localForage';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'services:RecentlyUsedSymbolsService' });

const RECENTLY_USED_SYMBOLS_MAX_AGE_DAYS = 7;

interface SymbolCacheEntry {
    symbol: string;
    lastUse: Instant;
}

@injectable()
export class RecentlyUsedSymbolsServiceImpl implements RecentlyUsedSymbolsService {
    #cache_key = SYMBOL_CACHE_KEY;
    #symbols: Signal<SymbolCacheEntry[]> = signal([]);

    #initialized: Promise<void>;

    constructor() {
        this.#initialized = this.#doInitialFetch();
    }

    async #doInitialFetch() {
        try {
            const symbolCache: SymbolCacheEntry[] | null | undefined = await localforage.getItem(this.#cache_key);
            if (symbolCache) {
                const expiresAt = Instant.now().minus(RECENTLY_USED_SYMBOLS_MAX_AGE_DAYS, ChronoUnit.DAYS);
                const validEntries = symbolCache.filter((entry) => {
                    const lastUse = typeof entry.lastUse === 'string' ? Instant.parse(entry.lastUse) : entry.lastUse;
                    return lastUse.isAfter(expiresAt);
                });
                this.#symbols.value = validEntries;
            }
        } catch (error) {
            log.error('Failed to initialize symbol cache', { error });
            this.#symbols.value = [];
        }

        if (TRACE) {
            log.debug({ message: `Initialized ${this.#cache_key} with ${this.#symbols.value.length} symbols` });
        }
    }

    async add(symbol: string) {
        if (TRACE) {
            log.debug({ message: `Adding symbol ${symbol} to ${this.#cache_key}` });
        }

        await this.#initialized;

        const currentEntries = this.#symbols.value;
        const filtered = currentEntries.filter((entry) => entry.symbol !== symbol);
        const updatedEntries = [{ symbol, lastUse: Instant.now() }, ...filtered];

        this.#symbols.value = updatedEntries;
        await localforage.setItem(this.#cache_key, updatedEntries);
    }

    async remove(symbol: string) {
        if (TRACE) {
            log.debug({ message: `Removing symbol ${symbol} from ${this.#cache_key}` });
        }

        await this.#initialized;

        const updatedEntries = this.#symbols.value.filter((entry) => entry.symbol !== symbol);
        this.#symbols.value = updatedEntries;
        await localforage.setItem(this.#cache_key, updatedEntries);
    }

    get symbols() {
        return this.#symbols as ReadonlySignal<SymbolCacheEntry[]>;
    }
}
